import React, { useCallback, useEffect, useState } from "react";
import { crmApi } from "../../lib/crmApi";
import { PageHead, inputCls, SERVICE_CATEGORIES, label } from "./crmUi";
import { LibraryMediaTab } from "./LibraryMediaTab";
import { VisualsWorkflowTab } from "./ConceptTasks";

const TABS = [["kb", "Knowledge Base"], ["media", "Reference Library"], ["visuals", "Visual Concepts"]];

function KbTab() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ kb_type: "article", title: "", body: "", approved: true, source: "" });
  const [test, setTest] = useState({ question: "", answer: null, busy: false });
  const [analytics, setAnalytics] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => crmApi.kbList(q ? `?q=${encodeURIComponent(q)}` : "").then((d) => { setItems(d.items); setTypes(d.types); }).catch((e) => setMsg(e.message)), [q]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await crmApi.kbCreate(form); setForm({ ...form, title: "", body: "" }); setMsg("Saved ✓"); load(); }
    catch (e) { setMsg(e.message); }
  };
  const runTest = async () => {
    setTest({ ...test, busy: true });
    try { const r = await crmApi.kbTest(test.question); setTest({ ...test, busy: false, answer: r }); }
    catch (e) { setTest({ ...test, busy: false }); setMsg(e.message); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="space-y-3">
        <input data-testid="kb-search" className={inputCls} placeholder="Search knowledge…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="space-y-2 max-h-[52vh] overflow-y-auto" data-testid="kb-list">
          {items.map((it) => (
            <div key={it.id} className="border border-[#6E1F2C]/15 bg-white p-3">
              <div className="flex justify-between items-start gap-2">
                <p className="text-[12px] font-medium text-[#1C110C]">{it.title}
                  <span className="ml-2 text-[9px] uppercase tracking-[0.12em] text-[#A6822F]">{it.kb_type}</span>
                </p>
                <div className="flex gap-1.5 shrink-0">
                  <button type="button" className={`text-[9px] uppercase px-2 py-0.5 border ${it.approved ? "text-[#3F6E2C] border-[#3F6E2C]/40" : "text-[#A3141A] border-[#A3141A]/40"}`}
                    onClick={() => crmApi.kbUpdate(it.id, { ...it, approved: !it.approved }).then(load)}>
                    {it.approved ? "Approved" : "Unapproved"}
                  </button>
                  <button type="button" className="text-[9px] uppercase px-2 py-0.5 border border-[#7A6B5F]/40 text-[#7A6B5F]"
                    onClick={() => crmApi.kbDelete(it.id).then(load)}>Delete</button>
                </div>
              </div>
              <p className="text-[11px] text-[#7A6B5F] mt-1">{(it.body || "").slice(0, 160)}…</p>
              <p className="text-[9px] text-[#7A6B5F] mt-1">Source: {it.source || "—"} · Updated {it.updated_at?.slice(0, 10)}</p>
            </div>
          ))}
          {items.length === 0 && <p className="text-xs text-[#7A6B5F] italic">No knowledge items.</p>}
        </div>
      </div>
      <div className="space-y-3">
        <div className="border border-[#6E1F2C]/15 bg-white p-4 space-y-2">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F]">Add knowledge</p>
          <select data-testid="kb-type" className={inputCls} value={form.kb_type} onChange={(e) => setForm({ ...form, kb_type: e.target.value })}>
            {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <input data-testid="kb-title" className={inputCls} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea data-testid="kb-body" rows={4} className={inputCls} placeholder="Content (approved company knowledge)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <input className={inputCls} placeholder="Source (URL or reference)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
          <label className="flex items-center gap-2 text-xs text-[#3E2A22]">
            <input type="checkbox" checked={form.approved} onChange={(e) => setForm({ ...form, approved: e.target.checked })} /> Approved for AI use
          </label>
          <button type="button" data-testid="kb-save" onClick={save} disabled={!form.title || !form.body}
            className="bg-[#6E1F2C] text-white px-4 py-2 text-[9px] tracking-[0.16em] uppercase disabled:opacity-50">Save item</button>
        </div>
        <div className="border border-[#D4A963]/40 bg-[#FAF5EC] p-4 space-y-2">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F]">Test an AI answer</p>
          <input data-testid="kb-test-question" className={inputCls} placeholder="e.g. How does the measurement visit work?" value={test.question} onChange={(e) => setTest({ ...test, question: e.target.value })} />
          <button type="button" data-testid="kb-test-run" onClick={runTest} disabled={test.busy || !test.question}
            className="border border-[#6E1F2C] text-[#6E1F2C] px-4 py-2 text-[9px] tracking-[0.16em] uppercase disabled:opacity-50">
            {test.busy ? "Thinking…" : "Test answer"}
          </button>
          {test.answer && (
            <div className="text-[12px] text-[#1C110C] space-y-1" data-testid="kb-test-answer">
              <p>{test.answer.answer}</p>
              {test.answer.confidence != null && (
                <div className="flex items-center gap-2" data-testid="kb-test-confidence">
                  <span className="text-[9px] uppercase text-[#7A6B5F]">Confidence</span>
                  <div className="flex-1 h-1.5 bg-[#6E1F2C]/10"><div className="h-1.5 bg-[#3F6E2C]" style={{ width: `${test.answer.confidence}%` }} /></div>
                  <span className="text-[10px] text-[#1C110C]">{test.answer.confidence}%</span>
                </div>
              )}
              <p className="text-[9px] text-[#7A6B5F]">Sources: {(test.answer.sources_used || []).map((s) => s.title).join("; ") || "none"}</p>
              {test.answer.missing_knowledge && <p className="text-[10px] text-[#A3141A]" data-testid="kb-test-missing">Missing: {test.answer.missing_knowledge}</p>}
              {test.answer.suggested_improvement && <p className="text-[10px] text-[#A6822F]" data-testid="kb-test-suggestion">Suggestion: {test.answer.suggested_improvement}</p>}
            </div>
          )}
        </div>
        <div className="border border-[#2C5F6E]/25 bg-white p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[9px] tracking-[0.2em] uppercase text-[#2C5F6E]">Knowledge analytics</p>
            <div className="flex gap-1.5">
              <button type="button" data-testid="kb-analytics-btn"
                onClick={() => crmApi.kbAnalytics().then(setAnalytics).catch((e) => setMsg(e.message))}
                className="border border-[#2C5F6E]/40 text-[#2C5F6E] px-2 py-1 text-[9px] uppercase tracking-[0.12em] hover:bg-[#2C5F6E]/10">Load</button>
              <button type="button" data-testid="kb-trends-btn" disabled={test.busy}
                onClick={async () => { setMsg("Generating trends digest…"); try { const r = await crmApi.kbTrends(); setMsg(`${r.created.length} trend drafts created — approve them below`); load(); } catch (e) { setMsg(e.message); } }}
                className="border border-[#A6822F]/40 text-[#A6822F] px-2 py-1 text-[9px] uppercase tracking-[0.12em] hover:bg-[#A6822F]/10">Trends digest</button>
            </div>
          </div>
          {analytics && (
            <div className="text-[11px] space-y-2" data-testid="kb-analytics">
              <p className="text-[#1C110C]">{analytics.total_queries} queries · {analytics.answer_rate != null ? `${analytics.answer_rate}% answered from KB` : "no data yet"}</p>
              {analytics.top_topics?.length > 0 && (
                <p className="text-[#7A6B5F]">Hot topics: {analytics.top_topics.slice(0, 8).map((t) => `${t.topic} (${t.count})`).join(", ")}</p>
              )}
              {analytics.unanswered?.length > 0 && (
                <div>
                  <p className="text-[9px] uppercase text-[#A3141A]">Knowledge gaps (no KB match)</p>
                  {analytics.unanswered.slice(0, 6).map((u, i) => <p key={i} className="text-[10px] text-[#1C110C] truncate">• {u.query}</p>)}
                </div>
              )}
              {analytics.most_used_articles?.length > 0 && (
                <div>
                  <p className="text-[9px] uppercase text-[#3F6E2C]">Most used articles</p>
                  {analytics.most_used_articles.slice(0, 6).map((a) => <p key={a.id} className="text-[10px] text-[#1C110C] truncate">• {a.title} — {a.usage_count}×</p>)}
                </div>
              )}
            </div>
          )}
        </div>
        {msg && <p className="text-[11px] text-[#A6822F]">{msg}</p>}
      </div>
    </div>
  );
}

const V_COLORS = { queued: "#A6822F", generating: "#A6822F", awaiting_approval: "#2C5F6E", sent: "#3F6E2C", failed: "#A3141A", send_failed: "#A3141A", rejected: "#7A6B5F" };

function VisualsTab() {
  return <VisualsWorkflowTab />;
}

export default function LibraryPage() {
  const [tab, setTab] = useState("kb");
  return (
    <div data-testid="crm-library-page">
      <PageHead eyebrow="AI Library" title="Knowledge, Media & Visual Concepts" />
      <div className="flex gap-1.5 mb-5">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" data-testid={`library-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3.5 py-2 text-[9px] tracking-[0.16em] uppercase border ${tab === k ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "border-[#6E1F2C]/30 text-[#6E1F2C]"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "kb" && <KbTab />}
      {tab === "media" && <LibraryMediaTab />}
      {tab === "visuals" && <VisualsTab />}
    </div>
  );
}
